/**
 * useSignaling (Client, JS)
 * - Baut eine Socket.IO Verbindung zum bestehenden Server auf (Pfad: /ws)
 * - Authentifiziert per JWT aus localStorage (handshake.auth.token)
 * - Stellt Sender für Offer/Answer/ICE/Hangup/Decline bereit
 * - Leitet empfangene Events via Handler-Callbacks weiter
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { API_BASE_URL } from '../config/api';

const WS_PATH = '/ws'; // muss mit Server-Setup übereinstimmen

const resolveSocketOrigin = () => {
  const explicitWs = process.env.REACT_APP_WS_URL;
  if (explicitWs) {
    return explicitWs;
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    // Default to same-origin in the browser to avoid DNS mismatches on subdomains.
    return window.location.origin;
  }

  if (!API_BASE_URL) return undefined;

  try {
    const url = new URL(API_BASE_URL);
    return `${url.protocol}//${url.host}`;
  } catch (_) {
    return API_BASE_URL;
  }
};

export function useSignaling(handlers = {}) {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('token') : null;
    const origin = resolveSocketOrigin();

    const options = {
      path: WS_PATH,
      transports: ['websocket'],
      autoConnect: true,
      reconnection: true,
      auth: token ? { token } : {}
    };

    const socket = origin ? io(origin, options) : io(options);

    socketRef.current = socket;

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    // Präsenz (optional – aktuell nicht separat emittiert, Server behandelt setOnline on connection)
    if (handlers.onUserOnline) socket.on('user-online', handlers.onUserOnline);
    if (handlers.onUserOffline) socket.on('user-offline', handlers.onUserOffline);

    // Call Flow
    if (handlers.onRinging) socket.on('ringing', handlers.onRinging);
    if (handlers.onBusy) socket.on('busy', handlers.onBusy);

    if (handlers.onCallOffer) socket.on('call-offer', handlers.onCallOffer);
    if (handlers.onCallAnswer) socket.on('call-answer', handlers.onCallAnswer);
    if (handlers.onIceCandidate) socket.on('ice-candidate', handlers.onIceCandidate);

    if (handlers.onHangup) socket.on('hangup', handlers.onHangup);
    if (handlers.onDecline) socket.on('decline', handlers.onDecline);

    if (handlers.onError) socket.on('error', handlers.onError);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);

      socket.off('user-online');
      socket.off('user-offline');
      socket.off('ringing');
      socket.off('busy');
      socket.off('call-offer');
      socket.off('call-answer');
      socket.off('ice-candidate');
      socket.off('hangup');
      socket.off('decline');
      socket.off('error');

      try { socket.disconnect(); } catch (_) { }
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // einmal verbinden

  // Sender
  const sendOffer = useCallback((payload) => {
    socketRef.current?.emit('call-offer', payload);
  }, []);

  const sendAnswer = useCallback((payload) => {
    socketRef.current?.emit('call-answer', payload);
  }, []);

  const sendIceCandidate = useCallback((payload) => {
    socketRef.current?.emit('ice-candidate', payload);
  }, []);

  const sendHangup = useCallback((payload) => {
    socketRef.current?.emit('hangup', payload);
  }, []);

  const sendDecline = useCallback((payload) => {
    socketRef.current?.emit('decline', payload);
  }, []);

  return useMemo(() => ({
    socket: socketRef.current,
    connected,
    sendOffer,
    sendAnswer,
    sendIceCandidate,
    sendHangup,
    sendDecline,
  }), [connected, sendOffer, sendAnswer, sendIceCandidate, sendHangup, sendDecline]);
}
